import type { MarkedVariable, Rect } from './types';

interface HighlightOverlayProps {
  highlights: Record<string, Rect[]>;
  variables: MarkedVariable[];
  fieldErrors: Record<string, string>;
  activeHighlight: string | null;
  showFields: boolean;
}

/** Absolutely-positioned overlay chips/rects painted over the rendered document. */
export function HighlightOverlay({
  highlights,
  variables,
  fieldErrors,
  activeHighlight,
  showFields,
}: HighlightOverlayProps) {
  return (
    // z-index must beat docx-preview's `article { z-index: 1 }` and the floating-image
    // fixups (up to z-index: 2), otherwise the document's own text layer paints ON TOP
    // of the overlay and bleeds through the "Show fields" chips.
    <div aria-hidden className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 10 }}>
      {Object.entries(highlights).flatMap(([name, rects]) => {
        const v = variables.find((x) => x.name === name);
        const isErr = !!fieldErrors[name];
        const isActive = activeHighlight === name;
        const hue = isErr ? 'var(--crimson)' : 'var(--selise-blue)';
        return rects.map((r, i) => {
          const showLabel = showFields && i === 0 && !!v;
          return (
            <div
              key={`${name}-${i}`}
              data-highlight-overlay
              data-active={isActive || undefined}
              title={showLabel ? v!.label : undefined}
              style={{
                position: 'absolute',
                left: r.x,
                top: r.y,
                width: r.w,
                height: r.h,
                borderRadius: 4,
                // "Show fields" must fully HIDE the original text: paint every line of
                // the span opaque white (the docx page is always white) and show the
                // field label only on the first line. Continuation lines stay blank
                // white so they read as "same field continues" without the text
                // bleeding through.
                backgroundColor: showFields
                  ? '#fff'
                  : `hsl(${hue} / ${isActive ? 0.2 : isErr ? 0.14 : 0.1})`,
                border: `2px solid hsl(${hue} / ${isActive ? 0.8 : 0.45})`,
                boxShadow: isActive ? `0 1px 10px hsl(${hue} / 0.25)` : 'none',
                overflow: 'hidden',
                display: showLabel ? 'flex' : undefined,
                alignItems: showLabel ? 'center' : undefined,
              }}
            >
              {showLabel && (
                <span
                  style={{
                    padding: '0 4px',
                    fontSize: 11,
                    lineHeight: 1,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: `hsl(${hue})`,
                  }}
                >
                  {v!.label}
                </span>
              )}
            </div>
          );
        });
      })}
    </div>
  );
}
